import { useState } from "react";
import { Button } from "../common/Button";
import { Modal } from "../common/Modal";
import { Field } from "../common/Field";
import { poolApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { vocabularyFor } from "../../services/productVocabulary";

/**
 * Put wax, propolis or pollen into a pool, correct it, or take it out
 * (#430 slice 2).
 *
 * ── WHY THIS IS A COMPONENT AND NOT THREE MORE MODALS ON THE PAGE ──
 *
 * HoneyPoolPage keeps its own add/correct/remove inline, and that is
 * fine there: ONE pool, one set of forms. Here the page renders a card
 * per product, so inline forms would mean three modals times however
 * many products an account has, with the open state keyed by product to
 * stop the wax dialog submitting pollen. Keyed dialog state is exactly
 * the shape that produces "I corrected the wrong balance", so the state
 * lives next to the product it belongs to instead.
 *
 * ── NO VARIETAL, AND THAT IS NOT AN OMISSION ──
 *
 * A varietal is the flower the nectar came from. Wax has no varietal,
 * and the server agrees: add_to_pool, correct_pool and remove_from_pool
 * resolve one only when product_type is Honey (#430), and V120 leaves
 * the column null for everything else. Offering the picker here would
 * ask a question with no true answer and then file the guess into an
 * append-only ledger.
 *
 * ── THE SAME THREE RULES THE HONEY FORMS LEARNED ──
 *
 * Each of these cost a report once already, and they are restated rather
 * than inherited because this form does not share their code:
 *
 *   · `!(pounds > 0)` rather than `!pounds`, because -40 is truthy. That
 *     exact value got past web's add form and was refused by the server,
 *     while mobile's copy already checked it -- the one-path shape.
 *   · Correct asks for the MEASURED TOTAL, not the difference. Working
 *     out the drift is the app's job, not the keeper's.
 *   · Remove has NO preselected disposition. Sold stock left as product
 *     and lost stock did not; a year on, that is the difference between
 *     a good season and a bad one, and the ledger is append-only.
 *
 * The words come from productVocabulary, so a wax form says poured and a
 * pollen form says packed. The amounts still read in POUNDS for all of
 * them -- that is the unit the pool is stored and measured in, and
 * inventing "blocks in the pool" would be a count the app cannot
 * actually produce from a weight.
 */
export function ProductPoolAdjuster({ productType, onChanged, children }) {
  const { notify } = useToast();
  const vocab = vocabularyFor(productType);
  /* Which of the three is open, or null. One at a time: they are three
     ways of saying what the pool holds, and two open at once invites
     typing the number into whichever is on top. */
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ pounds: "", reason: "" });
  const [correctForm, setCorrectForm] = useState({ pounds: "", reason: "" });
  const [removeForm, setRemoveForm] = useState({ pounds: "", disposition: "", reason: "" });

  const noun = String(productType || "").toLowerCase();
  /* "unpoured" -> "poured". The vocabulary stores the RAW state because
     that is what the balance line needs ("3.5 lbs unpoured"); the
     packaged state is the same word without its prefix, rather than a
     fifth field that could disagree with it. */
  const packaged = vocab.raw.replace(/^un/, "");

  function close() {
    setOpen(null);
    setAddForm({ pounds: "", reason: "" });
    setCorrectForm({ pounds: "", reason: "" });
    setRemoveForm({ pounds: "", disposition: "", reason: "" });
  }

  /* ONE submit path for all three. They differ only in which call they
     make and what they say afterwards, and writing that out three times
     is how one of them ends up not reloading the page. */
  async function run(call, sentence) {
    setBusy(true);
    try {
      const r = await call();
      notify(sentence(r), "success");
      close();
      await onChanged?.();
    } catch (err) {
      // The server's sentence wherever there is one: the over-draw
      // refusal names both numbers and points at Correct, and two
      // clients inventing their own wording is how they drift.
      notify(err.apiMessage || `Could not record that ${noun}`, "danger");
    } finally {
      setBusy(false);
    }
  }

  function submitAdd() {
    const pounds = Number(addForm.pounds);
    // -40 is truthy. See the note above.
    if (!(pounds > 0)) return notify(`Enter how many pounds of ${noun} went in`, "warning");
    return run(
      () => poolApi.add(pounds, addForm.reason || undefined, undefined, productType),
      (r) => `${productType} pool is now ${r.pool_pounds} lb`,
    );
  }

  function submitCorrect() {
    // "" is unanswered; 0 is a real and meaningful answer -- the pool is
    // empty -- so this cannot be a truthiness check either.
    if (correctForm.pounds === "") {
      return notify(`Enter how much ${noun} you actually have`, "warning");
    }
    return run(
      () => poolApi.correct(
        Number(correctForm.pounds), correctForm.reason || undefined, undefined, productType),
      (r) => (r.unchanged
        ? "That matches what the app had — nothing to change"
        : `${productType} corrected by ${r.adjusted_by} lb, now ${r.pool_pounds} lb`),
    );
  }

  function submitRemove() {
    const pounds = Number(removeForm.pounds);
    // The endpoint negates the sign it is given (V037), so a negative
    // here would be stock ARRIVING through the form for taking it out.
    if (!(pounds > 0)) return notify(`Enter how many pounds of ${noun} left the pool`, "warning");
    if (!removeForm.disposition) return notify("Say whether it was sold or lost", "warning");
    return run(
      () => poolApi.remove(
        pounds, removeForm.disposition, removeForm.reason || undefined, undefined, productType),
      (r) => `Removed ${pounds} lb — ${r.pool_pounds} lb left`,
    );
  }

  return (
    <>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
                    marginTop: "var(--space-3)" }}>
        {/* ADD first, because it is the one that happens most and the
            only one somebody arrives here intending to do. */}
        <Button size="sm" onClick={() => setOpen("add")}>{`Add ${noun}`}</Button>
        {/* The product's own verb (Pour, Pack, Bottle) sits right after Add,
            the order the Honey Pool uses (#518). */}
        {children}
        <Button size="sm" variant="secondary" onClick={() => setOpen("correct")}>
          Correct balance
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setOpen("remove")}>
          {`Remove ${noun}`}
        </Button>
      </div>

      <Modal
        open={open === "add"}
        onClose={close}
        title={`Add ${productType} to the Pool`}
        footer={<Button disabled={busy} onClick={submitAdd}>{busy ? "Saving…" : "Add"}</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field
            label="Pounds"
            /* Named for the product, because how it is MEASURED differs:
               wax comes off a melter, pollen out of a trap. Honey's hint
               here says "usually what came off an extraction", which
               means nothing on a wax screen. */
            hint={`However much ${noun} you now have to work with, before you ${vocab.verb.toLowerCase()} any.`}
          >
            {(id, aria) => (
              <input id={id} {...aria} type="number" step="0.0001" min="0"
                className="touch-target" value={addForm.pounds}
                onChange={(e) => setAddForm((f) => ({ ...f, pounds: e.target.value }))} />
            )}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => (
              <input id={id} {...aria} className="touch-target" value={addForm.reason}
                onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))} />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={open === "correct"}
        onClose={close}
        title={`Correct the ${productType} Pool`}
        footer={(
          <Button disabled={busy} onClick={submitCorrect}>
            {busy ? "Saving…" : "Save Correction"}
          </Button>
        )}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* THE MEASURED TOTAL, not the drift. */}
          <Field
            label={`How much ${noun} do you actually have?`}
            hint="The weight you just measured. The app works out the difference."
          >
            {(id, aria) => (
              <input id={id} {...aria} type="number" step="0.0001" min="0"
                className="touch-target" value={correctForm.pounds}
                onChange={(e) => setCorrectForm((f) => ({ ...f, pounds: e.target.value }))} />
            )}
          </Field>
          <Field
            label="Why (optional)"
            hint="Worth a word — a correction is the entry you come back to."
          >
            {(id, aria) => (
              <input id={id} {...aria} className="touch-target" value={correctForm.reason}
                onChange={(e) => setCorrectForm((f) => ({ ...f, reason: e.target.value }))} />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={open === "remove"}
        onClose={close}
        title={`Remove ${productType} from the Pool`}
        footer={<Button disabled={busy} onClick={submitRemove}>{busy ? "Saving…" : "Remove"}</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            {`For ${noun} that left the pool without being ${packaged} — `}
            sold in bulk, or spilled, spoiled or otherwise lost.
          </p>
          <Field label="Pounds">
            {(id, aria) => (
              <input id={id} {...aria} type="number" step="0.0001" min="0"
                className="touch-target" value={removeForm.pounds}
                onChange={(e) => setRemoveForm((f) => ({ ...f, pounds: e.target.value }))} />
            )}
          </Field>
          {/* NO PRESELECTED OPTION. See the note at the top. */}
          <Field label="What happened to it" required>
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={removeForm.disposition}
                onChange={(e) => setRemoveForm((f) => ({ ...f, disposition: e.target.value }))}>
                <option value="">Choose…</option>
                <option value="Sale">{`Sold — in bulk, before being ${packaged}`}</option>
                <option value="Loss">Lost — spilled, spoiled, or given away</option>
              </select>
            )}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => (
              <input id={id} {...aria} className="touch-target" value={removeForm.reason}
                onChange={(e) => setRemoveForm((f) => ({ ...f, reason: e.target.value }))} />
            )}
          </Field>
        </div>
      </Modal>
    </>
  );
}
